import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import api from "../../app/api";
import { AUTH_STATUS, useAuth, USER_NAME_ROLE } from "../../app/auth";
import LoginForm from "./forms/loginForm";
import LicenseImportForm from "./forms/licenseImportForm";
import axios from "axios";

const LOGIN_PAGE_FORMS = {
  LOGIN_FORM: "login",
  LICENSE_IMPORT: "lic_import",
};

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const authenticator = useAuth();

  const [formToRender, setFormToRender] = useState<string>(
    LOGIN_PAGE_FORMS.LOGIN_FORM
  );
  const [showRestoreOption, setShowRestoreOption] = useState<boolean>(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState<boolean>(false);
  const [isDbHealthy, setIsDbHealthy] = useState<boolean | null>(null);

  const [selectedOption, setSelectedOption] = useState<string>("initial");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const licenseInActiveParam = searchParams.get("inactive");
  const licenseInActiveFlag =
    licenseInActiveParam !== null &&
    licenseInActiveParam.trim().toLowerCase() === "true";

  const addSystemStatusLog = async (status: string, type:string) => {
    api.post("/system_status_logs/addSystemStatusLog.php", {
      created_by: sessionStorage.getItem(USER_NAME_ROLE) == "admin"
        ? 1
        : 2,  
      status,
      type
    })
      .then((res) => {
        console.log("System status log added successfully:", res.data);
      })
      .catch((error) => {
        console.error("Error adding system status log:", error);
      });
  }
  useEffect(() => {
    const checkDbHealth = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}database/db_health.php`
        );
        if (response.data.status === "ok") {
          setIsDbHealthy(true);
          // await addSystemStatusLog("Healthy", "Database Health");
        } else {
          setIsDbHealthy(false);
          // await addSystemStatusLog("Crashed", "Database Health");
        }
      } catch (error) {
        setIsDbHealthy(false);
        // await addSystemStatusLog("Crashed", "Database Health");
      }
    };

    checkDbHealth();
  }, []);

  useEffect(() => {
    switch (authenticator.authStatus) {
      case AUTH_STATUS.AUTHENITCATED:
        setFormToRender(
          licenseInActiveFlag
            ? LOGIN_PAGE_FORMS.LICENSE_IMPORT
            : LOGIN_PAGE_FORMS.LOGIN_FORM
        );
        setShowRestoreOption(true);
        break;
      case AUTH_STATUS.DATABASE_MALFORMED:
        setFormToRender(LOGIN_PAGE_FORMS.LOGIN_FORM);
        setShowRestoreOption(true);
        break;
      default:
        setFormToRender(LOGIN_PAGE_FORMS.LOGIN_FORM);
        setShowRestoreOption(true);
        break;
    }
  }, [authenticator.authStatus, licenseInActiveFlag]);

  const handleRestoreDatabase = async (type: "initial" | "backup") => {
    setLoading(true);
    try {
      // Step 1: Check DB health first
      const healthResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}database/db_restorehealth.php`
      );

      if (healthResponse.data.status !== "ok") {
        throw new Error("Database is not healthy. Restore operation aborted.");
      }

      // Step 2: If healthy, proceed with restore
      const restoreResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}database/restoreDatabase.php`,
        { type }
      );
      setSnackbar({
        open: true,
        message: `✅ Success: ${restoreResponse.data.message}`,
        severity: "success",
      });
      addSystemStatusLog("Success", "Restore Database");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || "Something went wrong";

      setSnackbar({
        open: true,
        message: `❌ Error: ${errorMessage}`,
        severity: "error",
      });
      addSystemStatusLog("Failed", "Restore Database");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.primary.main,
        height: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box>
        <Grid container>
          <Box
            sx={{
              height: { sm: "600px", xs: "100vh" },
              background: "#fff",
              display: "flex",
            }}
          >
            {/* Left image panel */}
            <Grid item sm={6} xs={0}>
              <Box
                sx={{
                  backgroundColor: "#7d95b4",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  display: { sm: "flex", xs: "none" },
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    mixBlendMode: "overlay",
                    overflowX: "hidden",
                    display: { sm: "block", xs: "none" },
                  }}
                >
                  <img
                    alt="Engine"
                    src="Engine_images.png"
                    height="100%"
                    width="100%"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Right form panel */}
            <Grid item sm={6} xs={12}>
              <Box
                sx={{
                  height: "100%",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                  p: 2,
                  overflowX: "hidden",
                }}
              >
                {showRestoreOption && isDbHealthy === false && (
                  <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => setRestoreDialogOpen(true)}
                    >
                      Restore Database
                    </Button>
                  </Box>
                )}

                {formToRender === LOGIN_PAGE_FORMS.LOGIN_FORM && <LoginForm isDbHealthy={isDbHealthy}/>}
                {formToRender === LOGIN_PAGE_FORMS.LICENSE_IMPORT && (
                  <LicenseImportForm />
                )}

                {/* Restore Dialog */}
                <Dialog
                  open={restoreDialogOpen}
                  onClose={() => setRestoreDialogOpen(false)}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle sx={{ fontSize: "1.5rem" }}>
                    Restore Database
                  </DialogTitle>
                  <DialogContent>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "error.main",
                        fontWeight: "bold",
                        mb: 3,
                        fontSize: "1rem",
                      }}
                    >
                      ⚠️ Ops! Database might be no accessible.<br></br>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        // color: "error.main",
                        fontWeight: "bold",
                        mb: 3,
                        fontSize: "1rem",
                      }}
                    >
                      Don't worry you can choose one of the following options to
                      restore the database.
                    </Typography>

                    <Box sx={{ ml: 1 }}>
                      <Typography
                        component="div"
                        sx={{ fontSize: "1.1rem", mb: 2 }}
                      >
                        <div style={{ marginBottom: "1rem" }}>
                          <strong>1.</strong>&nbsp;
                          <label>
                            <input
                              type="radio"
                              name="restoreOption"
                              value="initial"
                              checked={selectedOption === "initial"}
                              onChange={() => setSelectedOption("initial")}
                            />
                            &nbsp;Restore Database with Licence Info Only
                          </label>
                        </div>
                        <div>
                          <strong>2.</strong>&nbsp;
                          <label>
                            <input
                              type="radio"
                              name="restoreOption"
                              value="backup"
                              checked={selectedOption === "backup"}
                              onChange={() => setSelectedOption("backup")}
                            />
                            &nbsp;Restore Database with last Backup Data
                          </label>
                        </div>
                      </Typography>
                    </Box>
                  </DialogContent>

                  <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRestoreDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleRestoreDatabase(
                          selectedOption as "initial" | "backup"
                        );
                        setRestoreDialogOpen(false);
                      }}
                    >
                      Proceed
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Box>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={14000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            fontSize: "1.2rem", // Font size of the message
            padding: "16px 24px", // More padding
            minWidth: "400px", // Width of the snackbar
            borderRadius: "12px", // Optional: rounded corners
          },
        }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity as "success" | "error"}
          sx={{
            width: "100%",
            fontSize: "1.1rem", // Font size inside the alert
            padding: "14px 20px", // Custom padding for text
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Spinner while loading */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default LoginPage;
